import * as z from 'zod';

// Helper to convert string with commas to a number
const stringToNumber = (val: unknown) => {
  if (typeof val === 'string') {
    // Remove commas and any non-numeric characters except decimal point
    const cleaned = val.replace(/[^0-9.]/g, '');
    return cleaned ? Number(cleaned) : null;
  }
  return val as number | null;
};

// Asset schema based on backend API requirements
export const assetSchema = z.object({
  name: z.string().min(1, 'Name is required').max(255),
  asset_type: z.enum([
    'single_family',
    'condominium',
    'multi_family',
    'commercial',
    'industrial',
    'agricultural',
    'vacant_land',
    'mixed_use',
    'public_institutional',
    'recreational',
    'transportation',
  ]),
  address: z.string().optional(),
  entity_id: z
    .union([z.number(), z.string().transform(stringToNumber)])
    .nullable()
    .optional(),
  user_role: z.enum(['O', 'PM', 'I', 'A'] as const).default('O'),
  self_managed: z.boolean().default(true),
  management_platform: z
    .enum([
      'airbnb',
      'hemlane',
      'propertyware',
      'rentmanager',
      'cozy',
      'appfolio',
      'buildium',
      'homeroom',
      'quickbook',
      'yardi',
      'tenantcloud',
      'other',
      'none',
    ])
    .default('none'),
  purchase_date: z.string().optional().nullable(),
  purchase_price: z
    .union([z.number(), z.string().transform(stringToNumber)])
    .optional()
    .nullable(),
  market_value: z
    .union([z.number(), z.string().transform(stringToNumber)])
    .optional()
    .nullable(),
  market_rent: z
    .union([z.number(), z.string().transform(stringToNumber)])
    .optional()
    .nullable(),
  loan_amount: z
    .union([z.number(), z.string().transform(stringToNumber)])
    .optional()
    .nullable(),
  loan_due_date: z.string().optional().nullable(),
  down_payment: z
    .union([z.number(), z.string().transform(stringToNumber)])
    .optional()
    .nullable(),
  closing_costs: z
    .union([z.number(), z.string().transform(stringToNumber)])
    .optional()
    .nullable(),
  beds: z
    .union([z.number(), z.string().transform(stringToNumber)])
    .optional()
    .nullable(),
  baths: z
    .union([z.number(), z.string().transform(stringToNumber)])
    .optional()
    .nullable(),
  year_built: z
    .union([z.number(), z.string().transform(stringToNumber)])
    .optional()
    .nullable(),
  stories: z
    .union([z.number(), z.string().transform(stringToNumber)])
    .optional()
    .nullable(),
  units: z.union([z.number(), z.string().transform(stringToNumber)]).default(1),
  area_square_feet: z
    .union([z.number(), z.string().transform(stringToNumber)])
    .optional()
    .nullable(),
  lot_size_square_feet: z
    .union([z.number(), z.string().transform(stringToNumber)])
    .optional()
    .nullable(),
  land_percentage: z
    .union([z.number(), z.string().transform(stringToNumber)])
    .default(0),
  investment_status: z
    .enum(['active', 'closed', 'pending', 'sold'])
    .default('active'),
  investment_start_date: z.string().optional().nullable(),
  investment_end_date: z.string().optional().nullable(),
  notes: z.string().optional().nullable(),
});

export type AssetFormValues = z.infer<typeof assetSchema>;
